import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { supabase } from '@/integrations/supabase/client';

export function useContactActions(refetch: () => void) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenantId } = useTenantId();

  const addSampleContacts = async (setAddingSamples: (val: boolean) => void) => {
    if (!user || !tenantId) return;
    
    setAddingSamples(true);
    try {
      const sampleContacts = [
        {
          user_id: user.id,
          tenant_id: tenantId,
          name: 'John Smith',
          email: 'john.smith@email.com',
          phone: '(555) 123-4567',
          company: 'Smith Construction',
          contact_type: 'client',
          status: 'active',
          notes: 'Referred by previous client. Interested in kitchen renovation.',
          tags: ['referral', 'kitchen']
        },
        {
          user_id: user.id,
          tenant_id: tenantId,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@techcorp.com',
          phone: '(555) 234-5678',
          company: 'TechCorp Solutions',
          contact_type: 'vendor',
          status: 'active',
          notes: 'IT support vendor for office equipment.',
          tags: ['IT', 'vendor']
        },
        {
          user_id: user.id,
          tenant_id: tenantId,
          name: 'Mike Davis',
          email: 'mike.davis@gmail.com',
          phone: '(555) 345-6789',
          company: 'Davis Plumbing',
          contact_type: 'contractor',
          status: 'active',
          notes: 'Reliable plumber for emergency calls.',
          tags: ['plumbing', 'emergency']
        },
        {
          user_id: user.id,
          tenant_id: tenantId,
          name: 'Emily Wilson',
          email: 'emily.wilson@email.com',
          phone: '(555) 456-7890',
          company: 'Wilson Realty',
          contact_type: 'lead',
          status: 'active',
          notes: 'Potential client for bathroom remodel. Budget: $15k-20k.',
          tags: ['bathroom', 'remodel']
        },
      ];

      const { error } = await supabase
        .from('contacts')
        .insert(sampleContacts);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sample contacts added successfully"
      });
      
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add sample contacts",
        variant: "destructive"
      });
    } finally {
      setAddingSamples(false);
    }
  };

  const generateSampleCommunications = async (
    contacts: any[], 
    setGeneratingSamples: (val: boolean) => void,
    fetchCommunicationCounts: () => void
  ) => {
    if (!user || contacts.length === 0) return;
    
    setGeneratingSamples(true);
    try {
      const sampleCommunications = [];
      const now = new Date();
      
      for (const contact of contacts.slice(0, 5)) { // Limit to first 5 contacts
        const numComms = Math.floor(Math.random() * 3) + 3;
        
        for (let i = 0; i < numComms; i++) {
          const daysAgo = Math.floor(Math.random() * 7);
          const hoursAgo = Math.floor(Math.random() * 24);
          const timestamp = new Date(now.getTime() - (daysAgo * 24 + hoursAgo) * 60 * 60 * 1000);
          
          const commTypes = ['email', 'call', 'meeting', 'note'];
          const commType = commTypes[Math.floor(Math.random() * commTypes.length)];
          const direction = Math.random() > 0.5 ? 'inbound' : 'outbound';
          
          sampleCommunications.push({
            user_id: user.id,
            contact_id: contact.id,
            communication_type: commType,
            direction: direction,
            subject: commType === 'email' ? 'Project Update' : null,
            message: `Sample ${commType} communication`,
            status: 'completed',
            completed_at: timestamp.toISOString(),
            metadata: {}
          });
        }
      }
      
      const { error } = await supabase
        .from('contact_communications')
        .insert(sampleCommunications);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: `Generated ${sampleCommunications.length} sample communications`
      });
      
      fetchCommunicationCounts();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate sample communications",
        variant: "destructive"
      });
    } finally {
      setGeneratingSamples(false);
    }
  };

  return {
    addSampleContacts,
    generateSampleCommunications,
  };
}
